import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workersApi } from '../../api/workers';
import { queryKeys } from '../queryClient';
import type { CheckInRequest } from '../../types/domain';

export function useWorkers(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.workers, includeInactive],
    queryFn: () => workersApi.list(includeInactive),
  });
}

/** Module 3 admin oversight: profile + real job/earnings stats for one worker. */
export function useWorkerAdminDetail(workerId: string | undefined) {
  return useQuery({
    queryKey: ['workers', 'admin-detail', workerId ?? ''],
    queryFn: () => workersApi.adminDetail(workerId!),
    enabled: !!workerId,
  });
}

export function useSetWorkerActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, active }: { workerId: string; active: boolean }) => workersApi.setActive(workerId, active),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.workers });
      qc.invalidateQueries({ queryKey: ['workers', 'admin-detail', vars.workerId] });
    },
  });
}

export function useReviewWorkerVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, approve, rejectionReason }: { workerId: string; approve: boolean; rejectionReason?: string }) =>
      workersApi.reviewVerification(workerId, { approve, rejectionReason }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.workers });
      qc.invalidateQueries({ queryKey: ['workers', 'admin-detail', vars.workerId] });
    },
  });
}

export function useWorkerMe(enabled = true) {
  return useQuery({ queryKey: queryKeys.workerMe, queryFn: () => workersApi.me(), enabled, retry: false });
}

export function useWorkerDashboard(enabled = true) {
  return useQuery({
    queryKey: queryKeys.workerDashboard,
    queryFn: () => workersApi.myDashboard(),
    enabled,
    retry: false,
  });
}

export function useNearbyWorkers(lat: number, lng: number, radiusMeters = 5000, enabled = true) {
  return useQuery({
    queryKey: queryKeys.nearbyWorkers(lat, lng, radiusMeters),
    queryFn: () => workersApi.nearby(lat, lng, radiusMeters),
    enabled,
  });
}

export function useWorkerShifts(workerId: string | undefined, from?: string) {
  return useQuery({
    queryKey: queryKeys.shifts(workerId ?? ''),
    queryFn: () => workersApi.listShifts(workerId!, from),
    enabled: !!workerId,
  });
}

export function useCreateShift(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { startsAt: string; endsAt: string; notes?: string }) =>
      workersApi.createShift(workerId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.shifts(workerId) }),
  });
}

export function useCheckIn() {
  return useMutation({ mutationFn: (body: CheckInRequest) => workersApi.checkIn(body) });
}

export function useCheckOut() {
  return useMutation({ mutationFn: (checkInId: string) => workersApi.checkOut(checkInId) });
}

/** Manager+: one worker's payout history (admin oversight detail screen). */
export function useWorkerPayouts(workerId: string | undefined) {
  return useQuery({
    queryKey: ['workers', 'payouts', workerId ?? ''],
    queryFn: () => workersApi.payoutsFor(workerId!),
    enabled: !!workerId,
  });
}

/** Manager+: one worker's real job history (Module 3 admin detail). */
export function useWorkerJobs(workerId: string | undefined, status: 'upcoming' | 'completed') {
  return useQuery({
    queryKey: ['workers', 'jobs', workerId ?? '', status],
    queryFn: () => workersApi.jobsFor(workerId!, status),
    enabled: !!workerId,
  });
}

export function useCreatePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workerId, periodStart, periodEnd }: { workerId: string; periodStart: string; periodEnd: string }) =>
      workersApi.createPayout(workerId, periodStart, periodEnd),
    onSuccess: (_res, vars) => qc.invalidateQueries({ queryKey: ['workers', 'payouts', vars.workerId] }),
  });
}

export function useReleasePayout(workerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => workersApi.releasePayout(payoutId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers', 'payouts', workerId] }),
  });
}
